import {
  HairlineCard,
  Button,
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { isConfiguredBylawCategory, type FileStage as FileStageId } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { FileEmergencyBanner } from '../components/FileEmergencyBanner';
import { useAdvanceStage, useFile } from '../lib/files-api';
import { localizeStatuteRef, stageLabelKey, stageLegislativeRef } from '../lib/file-display';
import { IntakeStage } from './stages/IntakeStage';
import { ReviewStage } from './stages/ReviewStage';
import { SiteVisitStage } from './stages/SiteVisitStage';
import { ClassificationStage } from './stages/ClassificationStage';
import { EscalationStage } from './stages/EscalationStage';
import { ConfiguredWorkflowStage } from '../components/bylaw-workflow/ConfiguredWorkflowStage';
import { FileLoadError } from '../components/FileLoadError';

export function FileStage() {
  const { id = '', stage = 'intake' } = useParams();
  const stageId = stage as FileStageId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: file, isLoading, error } = useFile(id);
  const advance = useAdvanceStage(id);

  if (isLoading || !file) {
    return (
      <AppShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {isLoading ? <CircularProgress /> : <FileLoadError error={error} />}
        </Box>
      </AppShell>
    );
  }

  // Every workable stage has a dedicated work form; the generic advance shell
  // below is only a defensive fallback for the terminal/closed stages.
  if (stageId === 'intake') return <IntakeStage file={file} />;
  // A configured by-law type (property standards, noise, …) runs every stage
  // after intake from its definition. D&U, parking and other keep their
  // purpose-built screens below, unchanged.
  if (
    isConfiguredBylawCategory(file.bylawCategory) &&
    (stageId === 'review' ||
      stageId === 'site_visit' ||
      stageId === 'classification' ||
      stageId === 'escalation')
  ) {
    return <ConfiguredWorkflowStage file={file} stageId={stageId} />;
  }
  if (stageId === 'review') return <ReviewStage file={file} />;
  if (stageId === 'site_visit') return <SiteVisitStage file={file} />;
  if (stageId === 'classification') return <ClassificationStage file={file} />;
  if (stageId === 'escalation') return <EscalationStage file={file} />;

  const legislativeRef = stageLegislativeRef(file.bylawCategory, stageId);
  const onAdvance = () => advance.mutate(undefined, { onSuccess: () => navigate(`/files/${id}`) });

  return (
    <AppShell
      back={{ label: file.reference, onClick: () => navigate(`/files/${id}`) }}
      title={t(stageLabelKey(stageId))}
      bottomBar={
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={() => navigate(`/files/${id}`)}>
            {t('common.actions.back')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={advance.isPending || file.stage !== stageId}
            onClick={onAdvance}
          >
            {advance.isPending ? t('gov.stage.advancing') : t('gov.stage.complete')}
          </Button>
        </Stack>
      }
    >
      <FileEmergencyBanner emergency={file.emergency} />
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: fontStacks.mono }}>
        {file.reference}
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t(stageLabelKey(stageId))}</Typography>
        {legislativeRef && (
          <Typography variant="caption" color="text.secondary">
            {localizeStatuteRef(legislativeRef)}
          </Typography>
        )}
      </Stack>

      <HairlineCard sx={{ p: 3, mt: 2 }}>
        <Typography color="text.secondary">{t('gov.stage.placeholder')}</Typography>
      </HairlineCard>

      {file.stage !== stageId && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('gov.stage.notCurrent', { stage: t(stageLabelKey(file.stage)) })}
        </Alert>
      )}
    </AppShell>
  );
}
